import React, { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

/**
 * Setup Assistant create_location card: the location's name in an editable
 * field, then Confirm. The edited name goes through the same validation as
 * the proposal (sample names and duplicates are refused).
 */
export default function LocationCreateProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const summary = proposal.summary || {};
  const [name, setName] = useState(summary.store_name || "");
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loadingAction, setLoadingAction] = useState(null);
  const [error, setError] = useState(null);
  const [createdName, setCreatedName] = useState(null);

  React.useEffect(() => {
    if ((proposal.status === "cancelled" || proposal.status === "rejected") && status === "pending") {
      setStatus("cancelled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.status]);

  const trimmed = name.trim();

  const handleAction = async (action) => {
    setLoadingAction(action);
    setError(null);
    try {
      const payload = {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (action === "confirm" && trimmed && trimmed !== summary.store_name) {
        payload.overrides = { store_name: trimmed.slice(0, 100) };
      }
      const res = await postRightworkAPI(confirmUrl, payload);
      if (res.status === 200) {
        const result = res?.data?.redux?.payload?.result;
        if (action === "confirm" && result && typeof result === "object" && result.error) {
          setError(result.error);
          return;
        }
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setCreatedName(result?.store_name || trimmed || summary.store_name);
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      const message = err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.";
      setError(message);
    } finally {
      setLoadingAction(null);
    }
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 border bg-green-50 border-green-200 text-green-700">
        <CheckCircleIcon fontSize="small" className="mt-0.5 shrink-0 text-green-600" />
        <div className="min-w-0">
          <div className="font-medium">{summary.title || "Add location"}</div>
          <div className="text-xs mt-1">{createdName} added.</div>
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <AddLocationAltIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">{summary.title || "Add location"}</span>
      </div>
      <div className="px-3 py-3">
        <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor={`location-name-${proposal.proposal_id}`}>
          Name
        </label>
        <input
          id={`location-name-${proposal.proposal_id}`}
          type="text"
          maxLength={100}
          value={name}
          disabled={loadingAction !== null}
          onChange={(e) => setName(e.target.value)}
          className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
        />
        {error && (
          <div role="alert" className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={() => handleAction("reject")}
          disabled={loadingAction !== null}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleAction("confirm")}
          disabled={loadingAction !== null || !trimmed}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "confirm" && <CircularProgress size={14} sx={{ color: "white" }} />}
          Confirm
        </button>
      </div>
    </div>
  );
}
